import type { MapHitEvent, MapInteractionsConfig } from '@gpx-spa/geo-domain';
import { hitPayload, htmlSurface, isClusterHit, isMarkerHit } from '@gpx-spa/geo-domain';
import type { V2ShipmentDetail } from '@gpx-spa/queries/src/getAccountShipmentV2';
import type { Router } from 'vue-router';
import { parseListMarkerShipmentId } from './shipment-list-map-layers';
import {
  buildShipmentArcTooltipHtml,
  buildShipmentClusterTooltipHtml,
  buildShipmentEndpointTooltipHtml,
} from './shipment-list-map-surfaces';

function shipmentIdFromHit(
  event: MapHitEvent,
): number | null {
  if (isMarkerHit(event)) {
    return parseListMarkerShipmentId(event.target.marker.id);
  }
  if (event.target.type === 'feature') {
    const raw = hitPayload(event).shipmentId;
    const id = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  }
  return null;
}

function endpointLabelFromMarkerId(markerId: string): 'O' | 'D' | null {
  if (markerId.endsWith('-O')) return 'O';
  if (markerId.endsWith('-D')) return 'D';
  return null;
}

export function createShipmentListMapInteractions(
  shipmentsById: Map<number, V2ShipmentDetail>,
  router: Router,
): MapInteractionsConfig {
  return {
    triggers: ['click', 'hover'],
    cluster: { onClick: 'expand' },

    onHit(event) {
      if (event.trigger !== 'click') return;
      if (isClusterHit(event)) return;

      const shipmentId = shipmentIdFromHit(event);
      if (shipmentId == null) return;

      void router.push({
        name: 'ShipmentDetailsView',
        params: { id: shipmentId },
      });
    },

    surface(event) {
      if (event.trigger !== 'hover') return null;

      if (isMarkerHit(event)) {
        const shipmentId = parseListMarkerShipmentId(event.target.marker.id);
        const endpoint = endpointLabelFromMarkerId(event.target.marker.id);
        if (shipmentId == null || endpoint == null) return null;
        const shipment = shipmentsById.get(shipmentId);
        if (!shipment) return null;
        return htmlSurface(buildShipmentEndpointTooltipHtml(shipment, endpoint), {
          presentation: 'tooltip',
        });
      }

      if (event.target.type === 'feature') {
        const shipmentId = shipmentIdFromHit(event);
        if (shipmentId == null) return null;
        const shipment = shipmentsById.get(shipmentId);
        if (!shipment) return null;
        return htmlSurface(buildShipmentArcTooltipHtml(shipment), {
          presentation: 'tooltip',
        });
      }

      if (isClusterHit(event)) {
        return htmlSurface(
          buildShipmentClusterTooltipHtml(
            event.target.cluster.childMarkerIds,
            shipmentsById,
          ),
          { presentation: 'tooltip' },
        );
      }

      return null;
    },
  };
}
